import React from 'react';

import { Box, Button, Container, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import editorialWorkflowLarge from '../assets/images/editorial-workflow-1536.webp';
import editorialWorkflowSmall from '../assets/images/editorial-workflow-768.webp';
import BookCallLink from '../components/BookCallLink';
import Paragraph from '../components/Paragraph';
import SectionTitle from '../components/SectionTitle';
import TagList from '../components/TagList';

function Services() {
  return (
    <Container sx={{ '& p': { maxWidth: '70ch' } }}>
      <SectionTitle
        first='What we'
        second='build'
        type='titles'
        component='h1'
      />

      <Paragraph>
        Build the tools your team needs to manage content, connect information, and complete
        everyday work. We design web applications, CMS platforms, and integrations around the people
        using them and the systems they already rely on.
      </Paragraph>
      <Box
        component='section'
        aria-labelledby='engagement-overview'
        sx={{ mt: 3, mb: 3 }}
      >
        <Typography
          id='engagement-overview'
          component='h2'
          variant='h4'
          sx={{ color: '#0B2545', fontWeight: 700 }}
        >
          Start with your workflow
        </Typography>
        <Paragraph>
          We begin with your goals, users, and existing systems, then agree on a scope and proposal.
          Work progresses through reviewable build and test stages, with launch, handoff, and
          ongoing support planned for the engagement.
        </Paragraph>
        <Button href='#engagement-process'>See the delivery process</Button>
      </Box>

      <Box
        component='section'
        aria-labelledby='first-engagement'
        sx={{
          mt: 4,
          mb: 5,
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          border: '1px solid rgba(11, 37, 69, 0.14)',
          bgcolor: '#FFFFFF',
          scrollMarginTop: '5.5rem',
        }}
      >
        <Typography
          variant='overline'
          sx={{ color: '#005596', fontWeight: 700, display: 'block' }}
        >
          Defined first engagement
        </Typography>
        <Typography
          id='first-engagement'
          component='h2'
          variant='h4'
          sx={{ color: '#0B2545', fontWeight: 700, mb: 1.5 }}
        >
          Start here: CMS &amp; Workflow Assessment
        </Typography>
        <Paragraph>
          Whether you are improving an existing CMS or software platform, planning a new build, or
          connecting content with operational workflows, you can start with a bounded assessment
          before committing to a larger implementation.
        </Paragraph>
        <Paragraph>
          The assessment is a fixed-scope engagement, typically 2 to 3 weeks, with a fixed fee
          quoted after a short call based on the systems and workflows in scope.
        </Paragraph>
        <Typography
          component='h3'
          variant='h6'
          sx={{ color: '#0B2545', fontWeight: 700, mt: 2, mb: 1 }}
        >
          What you receive
        </Typography>
        <Box
          component='ul'
          sx={{
            m: 0,
            pl: 3,
            color: '#475569',
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr)',
            gap: 1,
            maxWidth: '70ch',
            overflowWrap: 'anywhere',
          }}
        >
          <li>
            Current-state workflow and editorial role map covering how content or requests move from
            preparation to publication.
          </li>
          <li>
            Architecture and data-flow diagram showing how your sites, applications, and connected
            systems fit together.
          </li>
          <li>
            Prioritized recommendations addressing governance, accessibility, or integration gaps.
          </li>
          <li>
            Written implementation roadmap and scoped proposal for next steps that your team can
            review before deciding on build work.
          </li>
        </Box>
        <Paragraph>
          After the assessment, your team keeps the written deliverables and decides whether to
          proceed with the proposed implementation scope.
        </Paragraph>
        <Button
          component={Link}
          to='/contact-us?subject=cms-workflow-assessment'
          data-track='assessment_request'
          variant='contained'
          sx={{
            mt: 1,
            backgroundColor: '#0B2545',
            color: '#FFFFFF',
            '&:hover': { backgroundColor: '#005596' },
          }}
        >
          Request a CMS &amp; workflow assessment
        </Button>
        <BookCallLink sx={{ mt: 1, ml: { sm: 2 } }} />
      </Box>

      <Box
        component='section'
        aria-labelledby='web-applications'
      >
        <SectionTitle
          id='web-applications'
          first='Custom web'
          second='applications'
          gutterBottom
        />
        <Paragraph>
          Give people a clear way to complete complex tasks. We design customer inquiries,
          availability checks, administrative portals, and approval workflows around the information
          users need and the decisions they make.
        </Paragraph>
        <TagList tags={['React', 'TypeScript', 'Next.js', '.NET', 'APIs']} />
        <Button
          component={Link}
          to='/case-studies'
          sx={{ mt: 1 }}
        >
          See application workflows
        </Button>
      </Box>

      <Box
        component='section'
        aria-labelledby='content-platforms'
      >
        <SectionTitle
          id='content-platforms'
          first='Content platforms &'
          second='CMS'
          gutterBottom
        />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.15fr) minmax(0, 1fr)' },
            alignItems: 'center',
            gap: { xs: 2, md: 4 },
            mb: 4,
          }}
        >
          <Box>
            <Paragraph>
              Give editors a clear way to create, review, and publish content. We plan CMS tools
              around your sites, languages, roles, and approval process, with reusable content
              structures and connections to existing systems.
            </Paragraph>
            <TagList
              tags={['Multi-site CMS', 'Editorial Roles', 'Multilingual Content', 'Media']}
            />
            <Paragraph>
              Our case studies distinguish predecessor publishing work from Scaled Tech delivery.
              InkFlow Studio is ongoing editorial-platform work; its production transition remains
              in progress.
            </Paragraph>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Button
                component={Link}
                to='/case-studies'
              >
                Explore CMS case studies
              </Button>
              <Button
                component={Link}
                to='/cms-governance'
              >
                Explore CMS governance
              </Button>
            </Box>
          </Box>
          <Box
            component='figure'
            sx={{
              m: 0,
              overflow: 'hidden',
              borderRadius: 3,
              border: '1px solid rgba(11, 37, 69, 0.12)',
              bgcolor: '#F8FAFC',
            }}
          >
            <img
              src={editorialWorkflowLarge}
              srcSet={`${editorialWorkflowSmall} 768w, ${editorialWorkflowLarge} 1536w`}
              sizes='(max-width: 900px) calc(100vw - 48px), 440px'
              width={1536}
              height={1024}
              loading='lazy'
              decoding='async'
              alt='Concept illustration of content editing, language variants, review roles, and a published page.'
              style={{ display: 'block', width: '100%', height: 'auto', aspectRatio: '3 / 2' }}
            />
            <Typography
              component='figcaption'
              variant='caption'
              sx={{ display: 'block', px: 2, py: 1.5, color: '#475569' }}
            >
              Editorial workflow
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        component='section'
        aria-labelledby='data-integrations'
      >
        <SectionTitle
          id='data-integrations'
          first='Data &'
          second='integrations'
          gutterBottom
        />
        <Paragraph>
          Make existing information useful where people work. We build APIs and data models to
          connect applications with external feeds, imports, and the information teams need. Earlier
          projects include searchable travel feeds and menu-data imports for staff to edit.
        </Paragraph>
        <TagList tags={['PostgreSQL', 'MySQL', 'SQL Server', 'XML', 'REST APIs']} />
        <Button
          component={Link}
          to='/solutions'
          sx={{ mt: 1 }}
        >
          Explore earlier integration work
        </Button>
      </Box>

      <Box
        component='section'
        aria-labelledby='deployment-operations'
      >
        <SectionTitle
          id='deployment-operations'
          first='Deployment &'
          second='operations'
          gutterBottom
        />
        <Paragraph>
          Plan how an application will be built, released, and maintained. We help define repeatable
          deployments, hosting choices, and operational handoffs, with support responsibilities and
          terms agreed for the engagement.
        </Paragraph>
        <TagList tags={['Docker', 'Cloudflare', 'Vercel', 'CI/CD']} />
        <Button
          component={Link}
          to='/about-us'
          sx={{ mt: 1 }}
        >
          Read our website delivery practices
        </Button>
      </Box>

      <Box
        component='section'
        aria-labelledby='cross-platform-applications'
      >
        <SectionTitle
          id='cross-platform-applications'
          first='Cross-platform'
          second='applications'
          gutterBottom
        />
        <Paragraph>
          Keep a workflow consistent across the devices people use. We plan interfaces,
          authentication, and data exchange around the needs of each platform. Our principal&apos;s
          earlier work includes mobile and web interfaces connected to enterprise services.
        </Paragraph>
        <TagList
          tags={{
            Desktop: ['Linux', 'Windows', 'macOS'],
            Web: ['Chrome', 'Firefox', 'Edge'],
            Mobile: ['Android', 'iOS'],
          }}
          sx={{ paddingTop: '5px' }}
        />
        <Button
          component={Link}
          to='/solutions'
          sx={{ mt: 1 }}
        >
          Explore earlier application work
        </Button>
      </Box>
      <Box
        component='section'
        aria-labelledby='engagement-process'
        sx={{ mt: 6 }}
      >
        <Typography
          id='engagement-process'
          component='h2'
          variant='h4'
          sx={{ fontWeight: 700, color: '#0B2545', mb: 2 }}
        >
          From inquiry to delivery
        </Typography>
        <Typography sx={{ color: '#475569', mb: 2 }}>
          Whether you are improving an existing CMS, planning a new platform, or looking for ongoing
          help, we shape the work around your goals and the people who use it.
        </Typography>
        <Box
          component='ol'
          sx={{ pl: 3, m: 0, color: '#475569' }}
        >
          <Box
            component='li'
            sx={{ mb: 2 }}
          >
            <Typography
              component='h3'
              variant='h6'
              sx={{ color: '#0B2545', fontWeight: 700 }}
            >
              Discovery and assessment
            </Typography>
            <Typography>
              After your inquiry, we discuss your goals, users, existing systems, and constraints.
              Together we identify the problems to address and information needed for next steps.
            </Typography>
          </Box>
          <Box
            component='li'
            sx={{ mb: 2 }}
          >
            <Typography
              component='h3'
              variant='h6'
              sx={{ color: '#0B2545', fontWeight: 700 }}
            >
              Scope and proposal
            </Typography>
            <Typography>
              If the work is a fit, we define a proposed scope, responsibilities, and approach for
              your review. Terms, timing, and costs are agreed for each engagement.
            </Typography>
          </Box>
          <Box
            component='li'
            sx={{ mb: 2 }}
          >
            <Typography
              component='h3'
              variant='h6'
              sx={{ color: '#0B2545', fontWeight: 700 }}
            >
              Build and test
            </Typography>
            <Typography>
              We build in reviewable stages and agree on testing for the features, content, and
              accessibility requirements in scope. Feedback helps guide corrections before launch.
            </Typography>
          </Box>
          <Box component='li'>
            <Typography
              component='h3'
              variant='h6'
              sx={{ color: '#0B2545', fontWeight: 700 }}
            >
              Launch and ongoing care
            </Typography>
            <Typography>
              We plan release and handoff with your team, including ownership and operating notes.
              Maintenance and support are agreed according to the engagement.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 3 }}>
          <Button
            component={Link}
            to='/case-studies'
          >
            See CMS case studies
          </Button>
          <Button
            component={Link}
            to='/contact-us'
            data-track='discuss_project'
          >
            Discuss your project
          </Button>
        </Box>
      </Box>
    </Container>
  );
}

export default Services;
